import {
  RangeSlider,
  RangeSliderFilledTrack,
  RangeSliderMark,
  RangeSliderThumb,
  RangeSliderTrack,
  Text,
} from '@chakra-ui/react';
import React, { useEffect } from 'react';

import {
  DEFAULT_BALLOON_RANGE,
  MAX_INTEREST,
  MIN_INTEREST,
  SLIDER_MODE1,
  SLIDER_STEP_MODE_5000,
} from '@/constants/payment-method/payment.constants';
import { useFinanceFilter } from '@/hooks/filters/finance-filter.hook';
import { usePriceFilter } from '@/hooks/filters/price-filter.hook';
import { useFiltersQuery } from '@/hooks/used-cars/filters-query.hook';
import { BalloonTypes, FinanceParts } from '@/types/filters/filters.types';

import { PriceFormat } from '../../../../price-format/price-format';
import { FinanceFooter } from '../finance-footer/finance-footer';
import { InputsSeparator } from '../inputs-separator/inputs-separator';
import { PaymentMethod } from '../payment-method/payment-method';
import { PaymentTerm } from '../payment-term/payment-term';
import { PriceInput } from '../price-input/price-input';
import { SectionName } from '../section-name/section-name';

import styles from './finance.module.scss';

interface PriceFilter {
  min: number;
  max: number;
}

export const Finance: React.FC<{
  initialDefaultPriceFilter?: PriceFilter;
  onSetFilter?: (filter: PriceFilter) => void;
}> = ({ initialDefaultPriceFilter, onSetFilter }) => {
  const {
    defaultPriceFilter,
    priceFilter,
    setPriceFilter,
    setSelectedPriceFilter,
    setDefaultPriceFilter,
  } = usePriceFilter();

  const { financeFilter, setFinanceFilter, activeBalloonOption } =
    useFinanceFilter();
  const { isCleared } = useFiltersQuery();

  useEffect(() => {
    if (!initialDefaultPriceFilter) return;
    setDefaultPriceFilter(initialDefaultPriceFilter);
  }, [initialDefaultPriceFilter]);

  useEffect(() => {
    setTimeout(() => {
      setFinanceFilter((previousFinanceFilter) => ({
        ...previousFinanceFilter,
        interest: 13.5,
      }));
    }, 0);

    setPriceFilter({
      min: defaultPriceFilter.min,
      max: defaultPriceFilter.max,
    });

    if (!onSetFilter) return;
    onSetFilter({
      min: defaultPriceFilter.min,
      max: defaultPriceFilter.max,
    });
  }, [isCleared]);

  const isPersentageMode = activeBalloonOption === BalloonTypes.percentage;
  const getBallonOption = () => {
    return {
      prefix: isPersentageMode ? '%' : 'R',
      marks: isPersentageMode ? balloonPercentageMarks : marks,
      max: isPersentageMode
        ? DEFAULT_BALLOON_RANGE.max
        : defaultPriceFilter.max,
      sliderStep: isPersentageMode ? SLIDER_MODE1 : SLIDER_STEP_MODE_5000,
    };
  };

  const marks = {
    [defaultPriceFilter.min]: {
      label: (
        <Text className={styles.priceFormatText}>
          <PriceFormat prefix="R" value={defaultPriceFilter.min} />
        </Text>
      ),
    },
    [defaultPriceFilter.max]: {
      label: (
        <Text className={styles.priceFormatText}>
          <PriceFormat prefix="R" value={defaultPriceFilter.max} />
        </Text>
      ),
    },
  };
  const interestMarks = {
    0: {
      label: (
        <Text className={styles.priceFormatText}>
          <PriceFormat suffix="%" value={MIN_INTEREST} />
        </Text>
      ),
    },
    [MAX_INTEREST]: {
      label: (
        <Text className={styles.priceFormatText}>
          <PriceFormat suffix="%" value={MAX_INTEREST} />
        </Text>
      ),
    },
  };

  const balloonPercentageMarks = {
    [DEFAULT_BALLOON_RANGE.min]: {
      label: (
        <Text className={styles.priceFormatText}>
          <PriceFormat suffix="%" value={DEFAULT_BALLOON_RANGE.min} />
        </Text>
      ),
    },
    [DEFAULT_BALLOON_RANGE.max]: {
      label: (
        <Text className={styles.priceFormatText}>
          <PriceFormat suffix="%" value={DEFAULT_BALLOON_RANGE.max} />
        </Text>
      ),
    },
  };

  const handleDepositChange = (value: number) => {
    if (value > defaultPriceFilter.max || value < defaultPriceFilter.min) {
      return;
    }

    setFinanceFilter((previousFinanceFilter) => ({
      ...previousFinanceFilter,
      deposit: value,
    }));
  };

  const handleBalloonChange = (value: number) => {
    const limits =
      activeBalloonOption === BalloonTypes.percentage
        ? DEFAULT_BALLOON_RANGE
        : defaultPriceFilter;

    if (value > limits.max || value < limits.min) {
      return;
    }

    setFinanceFilter((previousFinanceFilter) => ({
      ...previousFinanceFilter,
      balloon: value,
    }));
  };

  const handleInterestChange = (value: number) => {
    if (value > MAX_INTEREST || value < 0) {
      return;
    }

    setFinanceFilter((previousFinanceFilter) => ({
      ...previousFinanceFilter,
      interest: value,
    }));
  };

  const onSliderChange = (value: number, name: string) => {
    setFinanceFilter((previousFinanceFilter) => ({
      ...previousFinanceFilter,
      [name]: value,
    }));
  };

  const handleMinValue = (value: number) => {
    if (value > priceFilter.max || value < defaultPriceFilter.min) {
      return;
    }

    setPriceFilter((previousPriceFilter) => ({
      ...previousPriceFilter,
      min: value,
    }));

    setSelectedPriceFilter(() => ({
      ...priceFilter,
      min: value,
    }));

    if (!onSetFilter) return;
    onSetFilter({
      ...priceFilter,
      min: value,
    });
  };

  const handleMaxValue = (value: number) => {
    if (value < priceFilter.min || value > defaultPriceFilter.max) {
      return;
    }

    setPriceFilter(() => ({
      ...priceFilter,
      max: value,
    }));

    setSelectedPriceFilter((previousPriceFilter) => ({
      ...previousPriceFilter,
      max: value,
    }));

    setFinanceFilter((previousFinanceFilter) => ({
      ...previousFinanceFilter,
      price: value,
    }));

    if (!onSetFilter) return;
    onSetFilter({
      ...priceFilter,
      max: value,
    });
  };

  return (
    <div className={styles.financeWrapper}>
      <div className={styles.partWrapper}>
        <div className={styles.pricePartWrapper}>
          <div className={styles.sectionWrapper}>
            <SectionName>Price Range</SectionName>
            <div className={styles.rangesWrapper}>
              <PriceInput
                prefix="R"
                value={priceFilter.min || defaultPriceFilter.min}
                onChange={handleMinValue}
                name="minPrice"
                step={5000}
              />
              <InputsSeparator />
              <PriceInput
                prefix="R"
                value={priceFilter.max || defaultPriceFilter.max}
                onChange={handleMaxValue}
                name="maxPrice"
                step={5000}
              />
            </div>
          </div>

          <RangeSlider
            defaultValue={[defaultPriceFilter.min, defaultPriceFilter.max]}
            step={10000}
            mb={4}
            max={defaultPriceFilter.max}
            value={[
              priceFilter.min || defaultPriceFilter.min,
              priceFilter.max || defaultPriceFilter.max,
            ]}
            onChange={([min, max]) => {
              setPriceFilter({
                min: min ?? defaultPriceFilter.min,
                max: max ?? defaultPriceFilter.max,
              });
              setSelectedPriceFilter({
                min: min ?? defaultPriceFilter.min,
                max: max ?? defaultPriceFilter.max,
              });
              setFinanceFilter((previousFinanceFilter) => ({
                ...previousFinanceFilter,
                price: max ?? defaultPriceFilter.max,
              }));

              if (!onSetFilter) return;
              onSetFilter({
                min: min ?? defaultPriceFilter.min,
                max: max ?? defaultPriceFilter.max,
              });
            }}
          >
            <RangeSliderTrack bg="gray.100">
              <RangeSliderFilledTrack bg="black" />
            </RangeSliderTrack>
            <RangeSliderThumb
              boxSize={6}
              index={0}
              borderWidth="1px"
              borderColor="black"
            />
            <RangeSliderThumb
              boxSize={6}
              index={1}
              borderWidth="1px"
              borderColor="black"
            />
            <RangeSliderMark
              value={defaultPriceFilter.min}
              textAlign="left"
              mt={4}
            >
              <Text className={styles.priceFormatText}>
                <PriceFormat prefix="R" value={defaultPriceFilter.min} />
              </Text>
            </RangeSliderMark>
            <RangeSliderMark
              value={defaultPriceFilter.max}
              textAlign="right"
              width="max-content"
              transform="translateX(-100%)"
              mt={4}
            >
              <Text className={styles.priceFormatText}>
                <PriceFormat prefix="R" value={defaultPriceFilter.max} />
              </Text>
            </RangeSliderMark>
          </RangeSlider>
        </div>
        <PaymentMethod
          methodName="Deposit"
          prefix="R"
          value={financeFilter.deposit}
          onValueChange={handleDepositChange}
          onSliderChange={onSliderChange}
          sliderStep={5000}
          marks={marks}
          max={defaultPriceFilter.max}
          name={FinanceParts.deposit}
        />
        <PaymentMethod
          methodName="Balloon Payment"
          prefix={getBallonOption().prefix}
          value={financeFilter.balloon}
          onValueChange={handleBalloonChange}
          onSliderChange={onSliderChange}
          sliderStep={getBallonOption().sliderStep}
          marks={getBallonOption().marks}
          max={getBallonOption().max}
          name={FinanceParts.balloon}
        />
      </div>
      <div className={styles.partWrapper}>
        <PaymentMethod
          methodName="Interest Rate"
          prefix="%"
          value={financeFilter.interest}
          onValueChange={handleInterestChange}
          onSliderChange={onSliderChange}
          sliderStep={0.5}
          marks={interestMarks}
          max={MAX_INTEREST}
          name={FinanceParts.interest}
        />
        <PaymentTerm />
      </div>
      <FinanceFooter />
    </div>
  );
};
